import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import withRouter from "Components/Common/withRouter";
import ActionButton from "Components/atoms/buttons/action-button";
import LoadingState from "Components/organisms/loading-state";
import { CONTINUING_EDUCATION_STATUS } from "models/continuing-education-status";
import { SCREENS } from "models/screen";
import { ProfileSliceState } from "slices/auth/profile/reducer";
import { PermissionsSliceState } from "slices/auth/permissions/reducer";
import { getContinuingEducationRequest } from "slices/forms/continuing-education/thunk";
import { getPrintValues } from "utils/continuing-education";
import { navigateBackOrHome } from "utils/navigator";
import { renderTemplate } from "utils/print-template";

const selectProfile = createSelector((state: any) => state.Profile, ({ employee }: ProfileSliceState) => ({ employee }));
const selectPermissions = createSelector((state: any) => state.Permissions, ({ screens }: PermissionsSliceState) => ({ screens }));

const ContinuingEducationPrintPage = (props: any) => {
    document.title = "Print Continuing Education Request | IbexIQ";
    const id = props.router.params.id as string;
    const { employee } = useSelector(selectProfile);
    const { screens } = useSelector(selectPermissions);
    const frameRef = useRef<HTMLIFrameElement>(null);
    const [html, setHtml] = useState<string | null>(null);

    const initializePage = async () => {
        const request = await getContinuingEducationRequest(id);
        const isAdmin = screens.some(s => [SCREENS.ADMIN, SCREENS.HR_ADMIN, SCREENS.FIN_ADMIN].includes(s.code as SCREENS));
        const isParty = request && employee && [request.employeeId, request.managerApproverId, request.secondaryApproverId].includes(employee.id);

        if (!request || request.status?.description !== CONTINUING_EDUCATION_STATUS.APPROVED || (!isAdmin && !isParty)) {
            navigateBackOrHome(props.router.navigate)
            return;
        }
        setHtml(await renderTemplate("continuing-education-request.html", getPrintValues(request)))
    }

    useEffect(() => {
        initializePage()
    }, [])

    const handlePrint = () => frameRef.current?.contentWindow?.print()

    // Wait for images (the logo) before opening the print dialog.
    const handleFrameLoad = async () => {
        const frameDoc = frameRef.current?.contentDocument;
        if (!frameDoc) return;
        await Promise.all(Array.from(frameDoc.images).map(img => img.complete
            ? Promise.resolve()
            : new Promise(resolve => { img.onload = resolve; img.onerror = resolve; })));
        handlePrint()
    }

    if (!html) return <LoadingState title="Loading Request" subtitle="Preparing document…" />

    return <div style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "10px", display: "flex", justifyContent: "flex-end" }}>
            <ActionButton onClick={handlePrint}>Print / Save as PDF</ActionButton>
        </div>
        <iframe ref={frameRef} srcDoc={html} onLoad={handleFrameLoad} title="Continuing Education Request" style={{ flex: 1, border: 0 }} />
    </div>
}

export default withRouter(ContinuingEducationPrintPage);
